import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import SEOHead from "@/components/feature/SEOHead";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-emotion-sad-line text-5xl text-foreground-600"></i>
          </div>
          <h2 className="text-xl font-bold text-foreground-50 mb-2">
            文章不存在
          </h2>
          <Link
            to="/"
            className="text-sm text-primary-400 hover:text-primary-300 transition-colors duration-200"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter(
      (a) => a.categorySlug === article.categorySlug && a.id !== article.id
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-950">
      <SEOHead
        title={`${article.title} - 吃瓜黑料在线`}
        description={article.excerpt}
        keywords={`${article.tags.join(", ")},吃瓜,黑料,爆料,51吃瓜在线`}
        canonicalUrl={`https://www.chiguaheiliaozx.com/article/${article.slug}`}
        ogType="article"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: "吃瓜黑料在线", url: "https://www.chiguaheiliaozx.com" },
            url: `https://www.chiguaheiliaozx.com/article/${article.slug}`,
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.chiguaheiliaozx.com/article/${article.slug}` },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://www.chiguaheiliaozx.com" },
              { "@type": "ListItem", position: 2, name: article.category, item: `https://www.chiguaheiliaozx.com/category/${article.categorySlug}` },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          }),
        }}
      />
      {/* Article Header */}
      <article>
        <header className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-background-950"></div>

          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-8 md:pb-12">
              <div className="mb-3">
                <nav className="flex items-center gap-1.5 text-xs text-foreground-400">
                  <Link
                    to="/"
                    className="hover:text-primary-400 transition-colors duration-200 whitespace-nowrap"
                  >
                    首页
                  </Link>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-foreground-600"></i>
                  </div>
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="hover:text-primary-400 transition-colors duration-200 whitespace-nowrap"
                  >
                    {article.category}
                  </Link>
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-foreground-600"></i>
                  </div>
                  <span className="text-foreground-300 line-clamp-1">
                    {article.title}
                  </span>
                </nav>
              </div>
              <div className="inline-flex items-center gap-2 mb-3">
                <span className="px-2 py-0.5 bg-primary-500 text-foreground-50 text-xs rounded-full font-medium whitespace-nowrap">
                  {article.category}
                </span>
                {article.isHot && (
                  <span className="px-2 py-0.5 bg-accent-500 text-background-950 text-xs rounded-full font-medium whitespace-nowrap">
                    热门
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views >= 10000
                    ? `${(article.views / 10000).toFixed(0)}万`
                    : article.views}{" "}
                  阅读
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-message-2-line"></i>
                  </div>
                  {article.comments} 评论
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <div className="flex-1">
              <div
                className="prose prose-invert max-w-none text-sm text-foreground-200 leading-relaxed [&>p]:mb-4 [&>p]:leading-7"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-700">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-background-800 text-xs text-foreground-400 rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-background-700">
                <span className="text-xs text-foreground-500 whitespace-nowrap">
                  分享黑料：
                </span>
                <button className="w-8 h-8 flex items-center justify-center bg-background-800 hover:bg-primary-500 rounded-full transition-colors duration-200 cursor-pointer">
                  <i className="ri-weibo-line text-foreground-300 hover:text-foreground-50 text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center bg-background-800 hover:bg-primary-500 rounded-full transition-colors duration-200 cursor-pointer">
                  <i className="ri-wechat-line text-foreground-300 hover:text-foreground-50 text-sm"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center bg-background-800 hover:bg-primary-500 rounded-full transition-colors duration-200 cursor-pointer">
                  <i className="ri-link text-foreground-300 hover:text-foreground-50 text-sm"></i>
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:w-72 flex-shrink-0">
              {/* Author Card */}
              <div className="bg-background-900 border border-background-700 rounded-lg p-4 mb-5">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                  爆料来源
                </h4>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <i className="ri-user-line text-primary-500"></i>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-50">
                      {article.author}
                    </div>
                    <div className="text-xs text-foreground-500">
                      吃瓜黑料在线特约记者
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="bg-background-900 border border-background-700 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                    相关黑料
                  </h4>
                  <div className="flex flex-col gap-3">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.slug}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                          <img
                            src={rel.imageUrl}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h5 className="text-xs font-medium text-foreground-300 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2 mb-1">
                            {rel.title}
                          </h5>
                          <span className="text-[10px] text-foreground-600">
                            {rel.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </article>

      {/* Related Articles Grid */}
      {relatedArticles.length > 0 && (
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50 mb-6">
            更多{article.category}相关
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {relatedArticles.map((rel) => (
              <Link
                key={rel.id}
                to={`/article/${rel.slug}`}
                className="group bg-background-900 rounded-lg overflow-hidden cursor-pointer hover:bg-background-800 transition-colors duration-200"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={rel.imageUrl}
                    alt={rel.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-bold text-foreground-50 mb-2 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2">
                    {rel.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-1 mb-2">
                    {rel.excerpt}
                  </p>
                  <div className="text-xs text-foreground-600">{rel.date}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}